import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Upload, X, Save, Calendar, Users, Trophy, Heart, Filter, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';

const AdminGalerie = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('toutes');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    categorie: 'matchs',
    evenement: '',
    file: null
  });

  // Catégories disponibles
  const categories = [
    { id: 'toutes', name: 'Toutes les catégories', icon: <Filter size={16} /> },
    { id: 'matchs', name: 'Matchs', icon: <Trophy size={16} /> },
    { id: 'entrainements', name: 'Entraînements', icon: <Users size={16} /> },
    { id: 'evenements', name: 'Événements', icon: <Calendar size={16} /> },
    { id: 'supporters', name: 'Supporters', icon: <Heart size={16} /> }
  ];

  useEffect(() => {
    fetchPhotos();
  }, []);

  useEffect(() => {
    filterPhotos();
  }, [photos, selectedCategory, searchTerm]);

  const fetchPhotos = async () => {
    try {
      const photosQuery = query(
        collection(db, 'galerie'),
        orderBy('dateAjout', 'desc')
      );
      const snapshot = await getDocs(photosQuery);
      const photosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPhotos(photosData);
    } catch (error) {
      logger.error('Erreur lors du chargement des photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPhotos = () => {
    let filtered = photos;

    if (selectedCategory !== 'toutes') {
      filtered = filtered.filter(photo => photo.categorie === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(photo =>
        photo.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photo.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photo.evenement?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPhotos(filtered);
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      categorie: 'matchs',
      evenement: '',
      file: null
    });
    setEditingPhoto(null);
    setPreviewImage(null);
  };

  const openModal = (photo = null) => {
    if (photo) {
      setEditingPhoto(photo);
      setFormData({
        titre: photo.titre || '',
        description: photo.description || '',
        categorie: photo.categorie || 'matchs',
        evenement: photo.evenement || '',
        file: null
      });
      setPreviewImage(photo.url);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    const filename = `galerie/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filename);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titre.trim()) return;

    setUploading(true);
    try {
      let photoData = {
        titre: formData.titre.trim(),
        description: formData.description.trim(),
        categorie: formData.categorie,
        evenement: formData.evenement.trim(),
        dateAjout: new Date()
      };

      if (formData.file) {
        photoData.url = await uploadImage(formData.file);
        photoData.filename = formData.file.name;
      }

      if (editingPhoto) {
        // Supprimer l'ancienne image si une nouvelle est uploadée
        if (formData.file && editingPhoto.url) {
          try {
            const oldImageRef = ref(storage, editingPhoto.url);
            await deleteObject(oldImageRef);
          } catch (error) {
            logger.error('Erreur lors de la suppression de l\'ancienne image:', error);
          }
        }
        
        await updateDoc(doc(db, 'galerie', editingPhoto.id), photoData);
      } else {
        if (!formData.file) {
          throw new Error('Une image est requise pour créer une nouvelle photo');
        }
        await addDoc(collection(db, 'galerie'), photoData);
      }

      await fetchPhotos();
      closeModal();
    } catch (error) {
      logger.error('Erreur lors de l\'enregistrement:', error);
      toast.error('Erreur lors de l\'enregistrement de la photo', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    try {
      // Supprimer l'image du storage
      if (photo.url) {
        const imageRef = ref(storage, photo.url);
        await deleteObject(imageRef);
      }
      
      // Supprimer le document de Firestore
      await deleteDoc(doc(db, 'galerie', photo.id));
      await fetchPhotos();
      setDeleteConfirm(null);
    } catch (error) {
      logger.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de la photo', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#001c48] to-[#1A1A2E] p-4">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-gray-700 rounded-xl w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-700 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#001c48] to-[#1A1A2E] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-title text-3xl md:text-4xl font-bold text-white mb-2">
            Administration Galerie
          </h1>
          <p className="font-body text-gray-400">
            Gérez les photos de la galerie du club
          </p>
        </motion.div>

        {/* Actions et filtres */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 space-y-4"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <button
              onClick={() => openModal()}
              className="flex items-center space-x-2 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-6 py-3 rounded-xl font-accent font-bold hover:scale-105 transition-transform"
            >
              <Plus size={20} />
              <span>Ajouter une photo</span>
            </button>

            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl font-body text-white placeholder-gray-400 focus:outline-none focus:border-[#FFE400] transition-colors"
                />
              </div>

              {/* Filtre par catégorie */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl font-body text-white focus:outline-none focus:border-[#FFE400] transition-colors"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {categories.filter(c => c.id !== 'toutes').map(category => {
              const count = photos.filter(p => p.categorie === category.id).length;
              return (
                <div key={category.id} className="bg-gray-800/50 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center mb-2 text-[#FFE400]">
                    {category.icon}
                  </div>
                  <div className="font-title text-2xl font-bold text-white">{count}</div>
                  <div className="font-body text-xs text-gray-400">{category.name}</div>
                </div>
              );
            })}
            <div className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl p-4 text-center">
              <div className="font-title text-2xl font-bold text-[#001B56]">{photos.length}</div>
              <div className="font-body text-xs text-[#001B56]">Total</div>
            </div>
          </div>
        </motion.div>

        {/* Grille des photos */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {filteredPhotos.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📸</div>
              <h3 className="font-title text-2xl font-bold text-white mb-2">Aucune photo trouvée</h3>
              <p className="font-body text-gray-400">
                {searchTerm || selectedCategory !== 'toutes' 
                  ? 'Modifiez vos critères de recherche'
                  : 'Commencez par ajouter votre première photo'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredPhotos.map((photo) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="group relative bg-gray-800/50 rounded-xl overflow-hidden hover:scale-105 transition-transform"
                >
                  <div className="aspect-square relative">
                    <img
                      src={photo.url}
                      alt={photo.titre}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="font-title font-bold text-white text-sm mb-1">{photo.titre}</h3>
                        {photo.evenement && (
                          <p className="font-body text-[#FFE400] text-xs">{photo.evenement}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-accent font-medium bg-[#FFE400]/20 text-[#FFE400]">
                        {categories.find(c => c.id === photo.categorie)?.name || photo.categorie}
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openModal(photo)}
                          className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(photo)}
                          className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="font-body text-gray-400 text-sm">
                      {photo.dateAjout?.toDate?.()?.toLocaleDateString() || 'Date inconnue'}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Modal d'ajout/modification */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-[#00238D] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-title text-2xl font-bold text-white">
                    {editingPhoto ? 'Modifier la photo' : 'Ajouter une photo'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Upload d'image */}
                  <div>
                    <label className="block font-accent font-medium text-white mb-2">
                      Image {!editingPhoto && '*'}
                    </label>
                    <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 text-center hover:border-[#FFE400] transition-colors">
                      {previewImage ? (
                        <div className="relative">
                          <img
                            src={previewImage}
                            alt="Aperçu"
                            className="max-w-full h-48 object-contain mx-auto rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setPreviewImage(null);
                              setFormData(prev => ({ ...prev, file: null }));
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="mx-auto mb-4 text-gray-400" size={48} />
                          <p className="font-body text-gray-400 mb-2">
                            Cliquez pour choisir une image ou glissez-la ici
                          </p>
                          <p className="font-body text-gray-500 text-sm">
                            JPG, PNG ou WebP (max 10MB)
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                        </div>
                      )}
                    
                    </div>
                  </div>

                  {/* Titre */}
                  <div>
                    <label className="block font-accent font-medium text-white mb-2">
                      Titre *
                    </label>
                    <input
                      type="text"
                      value={formData.titre}
                      onChange={(e) => setFormData(prev => ({ ...prev, titre: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#001B56] border border-gray-700 rounded-xl font-body text-white placeholder-gray-400 focus:outline-none focus:border-[#FFE400] transition-colors"
                      placeholder="Titre de la photo"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block font-accent font-medium text-white mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#001B56] border border-gray-700 rounded-xl font-body text-white placeholder-gray-400 focus:outline-none focus:border-[#FFE400] transition-colors"
                      placeholder="Description de la photo"
                      rows={3}
                    />
                  </div>

                  {/* Catégorie */}
                  <div>
                    <label className="block font-accent font-medium text-white mb-2">
                      Catégorie *
                    </label>
                    <select
                      value={formData.categorie}
                      onChange={(e) => setFormData(prev => ({ ...prev, categorie: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#001B56] border border-gray-700 rounded-xl font-body text-white focus:outline-none focus:border-[#FFE400] transition-colors"
                      required
                    >
                      {categories.filter(c => c.id !== 'toutes').map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Événement */}
                  <div>
                    <label className="block font-accent font-medium text-white mb-2">
                      Événement
                    </label>
                    <input
                      type="text"
                      value={formData.evenement}
                      onChange={(e) => setFormData(prev => ({ ...prev, evenement: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#001B56] border border-gray-700 rounded-xl font-body text-white placeholder-gray-400 focus:outline-none focus:border-[#FFE400] transition-colors"
                      placeholder="Nom de l'événement (optionnel)"
                    />
                  </div>

                  {/* Boutons */}
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-xl font-accent font-medium hover:bg-gray-600 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={uploading || !formData.titre.trim() || (!editingPhoto && !formData.file)}
                      className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-6 py-3 rounded-xl font-accent font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#001B56] border-t-transparent"></div>
                          <span>Enregistrement...</span>
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          <span>{editingPhoto ? 'Modifier' : 'Ajouter'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal de confirmation de suppression */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={() => setDeleteConfirm(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-gray-900 rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-title text-xl font-bold text-white mb-4">
                  Confirmer la suppression
                </h3>
                <p className="font-body text-gray-300 mb-6">
                  Êtes-vous sûr de vouloir supprimer la photo "{deleteConfirm.titre}" ? 
                  Cette action est irréversible.
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-xl font-accent font-medium hover:bg-gray-600 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm)}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-accent font-medium hover:bg-red-700 transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminGalerie;